import type { PaginatedResult } from '@vipso/shared';
import { baseApi } from '../store/baseApi';

const SYSTEM_ADMIN_LOGIN = /* GraphQL */ `
  mutation SystemAdminLogin($email: String!, $password: String!) {
    systemAdminLogin(email: $email, password: $password) {
      accessToken
      refreshToken
      admin { id name email phone }
    }
  }
`;

const SYSTEM_ADMIN_INIT = /* GraphQL */ `
  mutation SystemAdminInit {
    systemAdminInit {
      admin { id name email phone masterRoleId masterRole { id name } }
      socketRooms
    }
  }
`;

const TENANTS = /* GraphQL */ `
  query Tenants($page: Int, $pageLimit: Int, $search: String) {
    tenants(page: $page, pageLimit: $pageLimit, search: $search) {
      rows {
        id name slug plan status
        config { defaultContextLogin loginType }
      }
      total page pageLimit
    }
  }
`;

const UPDATE_TENANT_CONFIG = /* GraphQL */ `
  mutation UpdateTenantConfig(
    $tenantId: ID!
    $defaultContextLogin: Boolean
    $loginType: String
    $maxUsers: Int
    $maxOrgs: Int
    $enabledModules: [String!]
  ) {
    updateTenantConfig(
      tenantId: $tenantId
      defaultContextLogin: $defaultContextLogin
      loginType: $loginType
      maxUsers: $maxUsers
      maxOrgs: $maxOrgs
      enabledModules: $enabledModules
    ) {
      defaultContextLogin loginType maxUsers maxOrgs enabledModules
    }
  }
`;

const ADMIN_UPSERT_ORG_CONFIG = /* GraphQL */ `
  mutation AdminUpsertOrgConfig(
    $orgId: ID!
    $defaultContextLogin: Boolean
    $loginType: String
    $maxUsers: Int
    $maxOrgs: Int
    $enabledModules: [String!]
  ) {
    adminUpsertOrgConfig(
      orgId: $orgId
      defaultContextLogin: $defaultContextLogin
      loginType: $loginType
      maxUsers: $maxUsers
      maxOrgs: $maxOrgs
      enabledModules: $enabledModules
    ) {
      id orgId defaultContextLogin loginType maxUsers maxOrgs enabledModules
    }
  }
`;

const ORGS = /* GraphQL */ `
  query Orgs($status: String, $page: Int, $pageLimit: Int, $search: String) {
    orgs(status: $status, page: $page, pageLimit: $pageLimit, search: $search) {
      rows { id name status phone uniqueId tenantId }
      total page pageLimit
    }
  }
`;

const ORG_DETAILS = /* GraphQL */ `
  query OrgDetails($orgId: ID!) {
    orgDetails(orgId: $orgId) {
      org {
        id name status phone uniqueId tenantId
        primaryColor logoFileId logoUrl
        operationsLocked paymentDueAt warningDays graceDays lockedAt lockReason
      }
      moduleCounts { module count }
      roles { id name featureKeys }
      staff { id userId name phone email roleName isActive isOwner }
      owners { id userId name phone email roleName isActive isOwner }
      featureKeys
      enabledModules
      featureSetFeatures {
        id featureSetId featureId featureKey name module isEnabled displayName
      }
      subscription {
        id status billingPeriod currentPeriodStart currentPeriodEnd
        bundleId seatCountOverride
      }
      invoices {
        id invoiceNumber status subtotal discountTotal total taxPct taxTotal grandTotal currency dueAt paidAt notes pdfFileId subscriptionId
        lines { id description quantity unitPrice lineTotal }
      }
      notifications {
        id message type priority isActive source sourceKey activeFrom activeTo orgId
      }
      settings {
        id code valueType stringValue booleanValue numberValue arrayValue enumOptions
      }
      metrics { key label used entitled }
      orgConfig {
        id orgId defaultContextLogin loginType maxUsers maxOrgs enabledModules
      }
      tenantConfig {
        defaultContextLogin loginType maxUsers maxOrgs enabledModules
      }
    }
  }
`;

const CREATE_CUSTOM_INVOICE = /* GraphQL */ `
  mutation CreateCustomBillingInvoice($input: JSON!) {
    createCustomBillingInvoice(input: $input) {
      id invoiceNumber status total taxPct taxTotal grandTotal currency dueAt
    }
  }
`;

const ADMIN_ADD_ORG_LICENSE = /* GraphQL */ `
  mutation AdminAddOrgLicense($input: JSON!) {
    adminAddOrgLicense(input: $input) {
      id status billingPeriod seatCountOverride currentPeriodStart currentPeriodEnd
      bundleId
    }
  }
`;

const SEED_BILLING_DEFAULTS = /* GraphQL */ `
  mutation SeedBillingDefaults {
    seedBillingDefaults
  }
`;

const UPSERT_SYSTEM_NOTIFICATION = /* GraphQL */ `
  mutation UpsertSystemNotification($input: JSON!) {
    upsertSystemNotification(input: $input) {
      id message type priority isActive orgId source
    }
  }
`;

const DELETE_SYSTEM_NOTIFICATION = /* GraphQL */ `
  mutation DeleteSystemNotification($id: ID!) {
    deleteSystemNotification(id: $id)
  }
`;

const UPDATE_ORG_BILLING_SETTINGS = /* GraphQL */ `
  mutation UpdateOrgBillingSettings($orgId: ID!, $warningDays: Int, $graceDays: Int) {
    updateOrgBillingSettings(orgId: $orgId, warningDays: $warningDays, graceDays: $graceDays) {
      id warningDays graceDays
    }
  }
`;

const ADMIN_UPSERT_ORG_SETTING = /* GraphQL */ `
  mutation AdminUpsertOrgSetting(
    $orgId: ID!
    $code: String!
    $valueType: String!
    $booleanValue: Boolean
    $stringValue: String
    $numberValue: Float
    $arrayValue: JSON
    $enumOptions: [String!]
  ) {
    adminUpsertOrgSetting(
      orgId: $orgId
      code: $code
      valueType: $valueType
      booleanValue: $booleanValue
      stringValue: $stringValue
      numberValue: $numberValue
      arrayValue: $arrayValue
      enumOptions: $enumOptions
    ) {
      id
      code
      valueType
      booleanValue
      stringValue
      numberValue
      arrayValue
      enumOptions
    }
  }
`;

const ADMIN_UPDATE_ORG_BRANDING = /* GraphQL */ `
  mutation AdminUpdateOrgBranding($orgId: ID!, $primaryColor: String, $logoFileId: ID) {
    adminUpdateOrgBranding(orgId: $orgId, primaryColor: $primaryColor, logoFileId: $logoFileId) {
      id primaryColor logoFileId logoUrl
    }
  }
`;

const UNLOCK_ORG_OPERATIONS = /* GraphQL */ `
  mutation UnlockOrgOperations($orgId: ID!) {
    unlockOrgOperations(orgId: $orgId) {
      id operationsLocked lockedAt lockReason
    }
  }
`;

const UPDATE_ORG_STATUS = /* GraphQL */ `
  mutation UpdateOrgStatus($orgId: ID!, $status: String!) {
    updateOrgStatus(orgId: $orgId, status: $status) { id status }
  }
`;

const CREATE_TENANT = /* GraphQL */ `
  mutation CreateTenant($name: String!, $slug: String!, $plan: String) {
    createTenant(name: $name, slug: $slug, plan: $plan) { id name slug }
  }
`;

const CREATE_ORG = /* GraphQL */ `
  mutation CreateOrg($tenantId: ID!, $name: String!, $phone: String, $status: String) {
    createOrg(tenantId: $tenantId, name: $name, phone: $phone, status: $status) {
      id name status
    }
  }
`;

const DELETE_ORG = /* GraphQL */ `
  mutation DeleteOrg($orgId: ID!) {
    deleteOrg(orgId: $orgId)
  }
`;

const SYSTEM_ADMINS = /* GraphQL */ `
  query SystemAdmins($page: Int, $pageLimit: Int, $search: String) {
    systemAdmins(page: $page, pageLimit: $pageLimit, search: $search) {
      rows {
        id name email phone masterRoleId
        masterRole { id name }
      }
      total page pageLimit
    }
  }
`;

const MASTER_ROLES = /* GraphQL */ `
  query MasterRoles {
    masterRoles { id name roleFor }
  }
`;

const ADD_UPDATE_MASTER_ROLE = /* GraphQL */ `
  mutation AddUpdateMasterRole($input: JSON!) {
    addUpdateMasterRole(input: $input) { id name roleFor }
  }
`;

const DELETE_MASTER_ROLE = /* GraphQL */ `
  mutation DeleteMasterRole($id: ID!) {
    deleteMasterRole(id: $id)
  }
`;

const CREATE_SYSTEM_ADMIN = /* GraphQL */ `
  mutation CreateSystemAdmin(
    $name: String!
    $email: String!
    $password: String!
    $phone: String
    $masterRoleId: ID
  ) {
    createSystemAdmin(
      name: $name
      email: $email
      password: $password
      phone: $phone
      masterRoleId: $masterRoleId
    ) { id name email phone masterRoleId }
  }
`;

const UPDATE_SYSTEM_ADMIN = /* GraphQL */ `
  mutation UpdateSystemAdmin(
    $id: ID!
    $name: String
    $email: String
    $phone: String
    $masterRoleId: ID
  ) {
    updateSystemAdmin(
      id: $id
      name: $name
      email: $email
      phone: $phone
      masterRoleId: $masterRoleId
    ) { id name email phone masterRoleId }
  }
`;

const DELETE_SYSTEM_ADMIN = /* GraphQL */ `
  mutation DeleteSystemAdmin($id: ID!) {
    deleteSystemAdmin(id: $id)
  }
`;

const UPDATE_PROFILE = /* GraphQL */ `
  mutation UpdateSystemAdminProfile($name: String, $phone: String) {
    updateSystemAdminProfile(name: $name, phone: $phone) {
      id name email phone
    }
  }
`;

const CHANGE_PASSWORD = /* GraphQL */ `
  mutation ChangeSystemAdminPassword($oldPassword: String!, $newPassword: String!) {
    changeSystemAdminPassword(oldPassword: $oldPassword, newPassword: $newPassword)
  }
`;

const AUDIT_LOGS = /* GraphQL */ `
  query AuditLogs($limit: Int) {
    auditLogs(limit: $limit)
  }
`;

const BROADCAST_ORG_REFRESH = /* GraphQL */ `
  mutation BroadcastOrgRefresh($orgId: ID!) {
    broadcastOrgRefresh(orgId: $orgId)
  }
`;

const FEATURES = /* GraphQL */ `
  query Features {
    features { id featureKey name module }
    featureSets {
      id name orgId isSystemDefault
      features { id featureSetId featureId featureKey name module isEnabled displayName }
    }
  }
`;

const ASSIGN_ORG_FEATURE_SET = /* GraphQL */ `
  mutation AssignOrgFeatureSet($orgId: ID!, $featureSetId: ID!) {
    assignOrgFeatureSet(orgId: $orgId, featureSetId: $featureSetId)
  }
`;

const UPDATE_FEATURE_SET_FEATURE = /* GraphQL */ `
  mutation UpdateFeatureSetFeature($id: ID!, $displayName: String, $isEnabled: Boolean) {
    updateFeatureSetFeature(id: $id, displayName: $displayName, isEnabled: $isEnabled) {
      id featureSetId featureId featureKey name module isEnabled displayName
    }
  }
`;

const UPDATE_FEATURE_SET_FEATURES = /* GraphQL */ `
  mutation UpdateFeatureSetFeatures($featureSetId: ID!, $items: [FeatureSetFeatureUpdateInput!]!) {
    updateFeatureSetFeatures(featureSetId: $featureSetId, items: $items) {
      id featureSetId featureId featureKey name module isEnabled displayName
    }
  }
`;

const TASK_TEMPLATES = /* GraphQL */ `
  query TaskTemplates($category: String) {
    taskTemplates(category: $category) {
      id code title description category kind assigneeType assigneeRoleKey
      priority actionKey sortOrder isActive
    }
  }
`;

const TASK_INSTANCES = /* GraphQL */ `
  query TaskInstances($orgId: ID, $status: String, $category: String) {
    taskInstances(orgId: $orgId, status: $status, category: $category) {
      id templateId orgId assigneeType assigneeId status priority
      subjectType subjectId completedAt resolutionNote createdAt
      template { id code title category kind priority actionKey }
    }
  }
`;

const UPSERT_TASK_TEMPLATE = /* GraphQL */ `
  mutation UpsertTaskTemplate($input: JSON!) {
    upsertTaskTemplate(input: $input) {
      id code title description category kind assigneeType assigneeRoleKey
      priority actionKey sortOrder isActive
    }
  }
`;

const BILLING_MODULES = /* GraphQL */ `
  query BillingModules {
    billingModules {
      id key name description meter monthlyPrice yearlyDiscountPct currency
      featureKeys enabledModuleTag isActive sortOrder
    }
  }
`;

const BILLING_OVERRIDES = /* GraphQL */ `
  query BillingOverrides($tenantId: ID!) {
    billingModulePriceOverrides(tenantId: $tenantId) {
      id tenantId moduleId monthlyPrice yearlyDiscountPct
      module { id key name meter monthlyPrice yearlyDiscountPct }
    }
  }
`;

const BILLING_BUNDLES = /* GraphQL */ `
  query BillingBundles {
    billingPlanBundles {
      id name slug description bundleDiscountPct isActive
      moduleIds
      modules { id key name meter monthlyPrice yearlyDiscountPct }
    }
  }
`;

const BILLING_SUBSCRIPTIONS = /* GraphQL */ `
  query BillingSubscriptions($orgId: ID, $status: String) {
    billingSubscriptions(orgId: $orgId, status: $status) {
      id tenantId orgId bundleId billingPeriod status seatCountOverride
      currentPeriodStart currentPeriodEnd activatedAt cancelledAt
      org { id name }
      items { id moduleId quantity unitPrice meter module { id key name } }
    }
  }
`;

const BILLING_INVOICES = /* GraphQL */ `
  query BillingInvoices($orgId: ID, $status: String) {
    billingInvoices(orgId: $orgId, status: $status) {
      id subscriptionId tenantId orgId invoiceNumber status
      subtotal discountTotal total taxPct taxTotal grandTotal currency
      periodStart periodEnd issuedAt dueAt paidAt markedPaidByAdminId notes pdfFileId
      org { id name }
      lines { id moduleId description quantity unitPrice lineTotal }
    }
  }
`;

const UPSERT_BILLING_MODULE = /* GraphQL */ `
  mutation UpsertBillingModule($input: JSON!) {
    upsertBillingModule(input: $input) {
      id key name description meter monthlyPrice yearlyDiscountPct currency
      featureKeys enabledModuleTag isActive sortOrder
    }
  }
`;

const UPSERT_BILLING_OVERRIDE = /* GraphQL */ `
  mutation UpsertBillingOverride($input: JSON!) {
    upsertBillingModulePriceOverride(input: $input) {
      id tenantId moduleId monthlyPrice yearlyDiscountPct
    }
  }
`;

const DELETE_BILLING_OVERRIDE = /* GraphQL */ `
  mutation DeleteBillingOverride($id: ID!) {
    deleteBillingModulePriceOverride(id: $id)
  }
`;

const UPSERT_BILLING_BUNDLE = /* GraphQL */ `
  mutation UpsertBillingBundle($input: JSON!) {
    upsertBillingPlanBundle(input: $input) {
      id name slug description bundleDiscountPct isActive moduleIds
      modules { id key name }
    }
  }
`;

const CREATE_BILLING_SUBSCRIPTION = /* GraphQL */ `
  mutation CreateBillingSubscription($input: JSON!) {
    createBillingSubscription(input: $input) {
      subscription { id status orgId billingPeriod }
      invoice { id invoiceNumber status total currency }
    }
  }
`;

const MARK_INVOICE_PAID = /* GraphQL */ `
  mutation MarkInvoicePaid($invoiceId: ID!) {
    markBillingInvoicePaid(invoiceId: $invoiceId) {
      id status paidAt total invoiceNumber
    }
  }
`;

const VOID_INVOICE = /* GraphQL */ `
  mutation VoidInvoice($invoiceId: ID!) {
    voidBillingInvoice(invoiceId: $invoiceId)
  }
`;

const CANCEL_SUBSCRIPTION = /* GraphQL */ `
  mutation CancelSubscription($id: ID!) {
    cancelBillingSubscription(id: $id) { id status cancelledAt }
  }
`;

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  config?: {
    defaultContextLogin: boolean;
    loginType: string;
  } | null;
}

export interface Org {
  id: string;
  name: string;
  status: string;
  phone?: string | null;
  uniqueId?: string | null;
  tenantId: string;
  primaryColor?: string | null;
  logoFileId?: string | null;
  logoUrl?: string | null;
  operationsLocked?: boolean;
  paymentDueAt?: string | null;
  warningDays?: number;
  graceDays?: number;
  lockedAt?: string | null;
  lockReason?: string | null;
}

export interface OrgStaffSummary {
  id: string;
  userId: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  roleName?: string | null;
  isActive: boolean;
  isOwner: boolean;
}

export interface SystemNotification {
  id: string;
  message: string;
  type: 'INFO' | 'WARNING' | 'CRITICAL' | 'MAINTENANCE';
  priority: number;
  activeFrom?: string | null;
  activeTo?: string | null;
  isActive: boolean;
  orgId?: string | null;
  source: string;
  sourceKey?: string | null;
}

export interface OrgMetric {
  key: string;
  label: string;
  used: number;
  entitled?: number | null;
}

export interface OrgSettingRow {
  id: string;
  code: string;
  valueType: string;
  stringValue?: string | null;
  booleanValue?: boolean | null;
  numberValue?: number | null;
  arrayValue?: unknown[] | null;
  enumOptions?: string[] | null;
}

export interface MasterRole {
  id: string;
  name: string;
  roleFor: string;
}

export interface SystemAdminRecord {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  masterRoleId?: string | null;
  masterRole?: { id: string; name: string } | null;
}

export type PaginatedList<T> = PaginatedResult<T>;

export interface FeatureSetFeatureRow {
  id: string;
  featureSetId: string;
  featureId: string;
  featureKey: string;
  name?: string | null;
  module?: string | null;
  isEnabled: boolean;
  displayName?: string | null;
}

export interface OrgConfigPayload {
  id: string;
  orgId: string;
  defaultContextLogin: boolean;
  loginType: string;
  maxUsers: number;
  maxOrgs: number;
  enabledModules: string[];
}

export interface TenantConfigPayload {
  defaultContextLogin: boolean;
  loginType: string;
  maxUsers?: number | null;
  maxOrgs?: number | null;
  enabledModules?: string[] | null;
}

export interface OrgDetailsPayload {
  org: Org;
  moduleCounts: Array<{ module: string; count: number }>;
  roles: Array<{ id: string; name: string; featureKeys?: string[] | null }>;
  staff: OrgStaffSummary[];
  owners: OrgStaffSummary[];
  featureKeys: string[];
  enabledModules: string[];
  featureSetFeatures: FeatureSetFeatureRow[];
  subscription?: BillingSubscription | null;
  invoices: BillingInvoice[];
  notifications: SystemNotification[];
  settings: OrgSettingRow[];
  metrics: OrgMetric[];
  orgConfig?: OrgConfigPayload | null;
  tenantConfig?: TenantConfigPayload | null;
}

export interface TaskTemplate {
  id: string;
  code: string;
  title: string;
  description?: string | null;
  category: string;
  kind: string;
  assigneeType: string;
  assigneeRoleKey?: string | null;
  priority: string;
  actionKey: string;
  sortOrder: number;
  isActive: boolean;
}

export interface TaskInstance {
  id: string;
  templateId: string;
  orgId: string;
  assigneeType: string;
  assigneeId: string;
  status: string;
  priority: string;
  subjectType?: string | null;
  subjectId?: string | null;
  completedAt?: string | null;
  resolutionNote?: string | null;
  createdAt?: string | null;
  template?: TaskTemplate | null;
}

export interface BillingModule {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  meter: string;
  monthlyPrice: number;
  yearlyDiscountPct: number;
  currency: string;
  featureKeys: string[];
  enabledModuleTag?: string | null;
  isActive: boolean;
  sortOrder: number;
}

export interface BillingModulePriceOverride {
  id: string;
  tenantId: string;
  moduleId: string;
  monthlyPrice?: number | null;
  yearlyDiscountPct?: number | null;
  module?: BillingModule | null;
}

export interface BillingPlanBundle {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  bundleDiscountPct: number;
  isActive: boolean;
  moduleIds: string[];
  modules: BillingModule[];
}

export interface BillingSubscription {
  id: string;
  tenantId: string;
  orgId: string;
  bundleId?: string | null;
  billingPeriod: string;
  status: string;
  seatCountOverride?: number | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  activatedAt?: string | null;
  cancelledAt?: string | null;
  org?: { id: string; name: string } | null;
  items: Array<{
    id: string;
    moduleId: string;
    quantity: number;
    unitPrice: number;
    meter: string;
    module?: { id: string; key: string; name: string } | null;
  }>;
}

export interface BillingInvoice {
  id: string;
  subscriptionId?: string | null;
  tenantId: string;
  orgId: string;
  invoiceNumber: string;
  status: string;
  subtotal: number;
  discountTotal: number;
  total: number;
  taxPct?: number;
  taxTotal?: number;
  grandTotal?: number;
  currency: string;
  periodStart: string;
  periodEnd: string;
  issuedAt: string;
  dueAt?: string | null;
  paidAt?: string | null;
  markedPaidByAdminId?: string | null;
  notes?: string | null;
  pdfFileId?: string | null;
  org?: { id: string; name: string } | null;
  lines: Array<{
    id: string;
    moduleId?: string | null;
    description: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
  }>;
}

export const adminApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    systemAdminLogin: build.mutation<
      {
        systemAdminLogin: {
          accessToken: string;
          refreshToken: string;
          admin: SystemAdminRecord;
        };
      },
      { email: string; password: string }
    >({
      query: (variables) => ({ document: SYSTEM_ADMIN_LOGIN, variables }),
    }),
    systemAdminInit: build.mutation<
      {
        systemAdminInit: {
          admin: SystemAdminRecord;
          socketRooms: string[];
        };
      },
      void
    >({
      query: () => ({ document: SYSTEM_ADMIN_INIT }),
    }),
    getTenants: build.query<
      { tenants: PaginatedList<Tenant> },
      { page?: number; pageLimit?: number; search?: string } | void
    >({
      query: (variables) => ({ document: TENANTS, variables: variables ?? {} }),
      providesTags: ['Tenant'],
    }),
    getOrgs: build.query<
      { orgs: PaginatedList<Org> },
      { status?: string; page?: number; pageLimit?: number; search?: string } | void
    >({
      query: (variables) => ({ document: ORGS, variables: variables ?? {} }),
      providesTags: ['Org'],
    }),
    getOrgDetails: build.query<{ orgDetails: OrgDetailsPayload }, { orgId: string }>({
      query: (variables) => ({ document: ORG_DETAILS, variables }),
      providesTags: ['Org'],
    }),
    updateOrgStatus: build.mutation<
      { updateOrgStatus: { id: string; status: string } },
      { orgId: string; status: string }
    >({
      query: (variables) => ({ document: UPDATE_ORG_STATUS, variables }),
      invalidatesTags: ['Org'],
    }),
    createTenant: build.mutation<
      { createTenant: Pick<Tenant, 'id' | 'name' | 'slug'> },
      { name: string; slug: string; plan?: string }
    >({
      query: (variables) => ({ document: CREATE_TENANT, variables }),
      invalidatesTags: ['Tenant'],
    }),
    updateTenantConfig: build.mutation<
      {
        updateTenantConfig: TenantConfigPayload;
      },
      {
        tenantId: string;
        defaultContextLogin?: boolean;
        loginType?: string;
        maxUsers?: number;
        maxOrgs?: number;
        enabledModules?: string[];
      }
    >({
      query: (variables) => ({ document: UPDATE_TENANT_CONFIG, variables }),
      invalidatesTags: ['Tenant', 'Org'],
    }),
    adminUpsertOrgConfig: build.mutation<
      { adminUpsertOrgConfig: OrgConfigPayload },
      {
        orgId: string;
        defaultContextLogin?: boolean;
        loginType?: string;
        maxUsers?: number;
        maxOrgs?: number;
        enabledModules?: string[];
      }
    >({
      query: (variables) => ({ document: ADMIN_UPSERT_ORG_CONFIG, variables }),
      invalidatesTags: ['Org'],
    }),
    createOrg: build.mutation<
      { createOrg: { id: string; name: string; status?: string } },
      { tenantId: string; name: string; phone?: string; status?: string }
    >({
      query: (variables) => ({ document: CREATE_ORG, variables }),
      invalidatesTags: ['Org'],
    }),
    deleteOrg: build.mutation<{ deleteOrg: Record<string, unknown> }, { orgId: string }>({
      query: (variables) => ({ document: DELETE_ORG, variables }),
      invalidatesTags: ['Org'],
    }),
    getSystemAdmins: build.query<
      { systemAdmins: PaginatedList<SystemAdminRecord> },
      { page?: number; pageLimit?: number; search?: string } | void
    >({
      query: (variables) => ({ document: SYSTEM_ADMINS, variables: variables ?? {} }),
      providesTags: ['SystemAdmin'],
    }),
    getMasterRoles: build.query<{ masterRoles: MasterRole[] }, void>({
      query: () => ({ document: MASTER_ROLES }),
      providesTags: ['SystemAdmin'],
    }),
    addUpdateMasterRole: build.mutation<
      { addUpdateMasterRole: MasterRole },
      { id?: string; name: string; roleFor?: string }
    >({
      query: (input) => ({ document: ADD_UPDATE_MASTER_ROLE, variables: { input } }),
      invalidatesTags: ['SystemAdmin'],
    }),
    deleteMasterRole: build.mutation<{ deleteMasterRole: Record<string, unknown> }, { id: string }>({
      query: (variables) => ({ document: DELETE_MASTER_ROLE, variables }),
      invalidatesTags: ['SystemAdmin'],
    }),
    createSystemAdmin: build.mutation<
      { createSystemAdmin: SystemAdminRecord },
      {
        name: string;
        email: string;
        password: string;
        phone?: string;
        masterRoleId?: string;
      }
    >({
      query: (variables) => ({ document: CREATE_SYSTEM_ADMIN, variables }),
      invalidatesTags: ['SystemAdmin'],
    }),
    updateSystemAdmin: build.mutation<
      { updateSystemAdmin: SystemAdminRecord },
      {
        id: string;
        name?: string;
        email?: string;
        phone?: string;
        masterRoleId?: string | null;
      }
    >({
      query: (variables) => ({ document: UPDATE_SYSTEM_ADMIN, variables }),
      invalidatesTags: ['SystemAdmin'],
    }),
    deleteSystemAdmin: build.mutation<{ deleteSystemAdmin: Record<string, unknown> }, { id: string }>({
      query: (variables) => ({ document: DELETE_SYSTEM_ADMIN, variables }),
      invalidatesTags: ['SystemAdmin'],
    }),
    updateSystemAdminProfile: build.mutation<
      { updateSystemAdminProfile: SystemAdminRecord },
      { name?: string; phone?: string }
    >({
      query: (variables) => ({ document: UPDATE_PROFILE, variables }),
      invalidatesTags: ['SystemAdmin'],
    }),
    changeSystemAdminPassword: build.mutation<
      { changeSystemAdminPassword: Record<string, unknown> },
      { oldPassword: string; newPassword: string }
    >({
      query: (variables) => ({ document: CHANGE_PASSWORD, variables }),
    }),
    getAuditLogs: build.query<{ auditLogs: Record<string, unknown>[] }, { limit?: number } | void>({
      query: (variables) => ({ document: AUDIT_LOGS, variables: variables ?? {} }),
      providesTags: ['AuditLog'],
    }),
    broadcastOrgRefresh: build.mutation<{ broadcastOrgRefresh: boolean }, { orgId: string }>({
      query: (variables) => ({ document: BROADCAST_ORG_REFRESH, variables }),
    }),
    getFeatures: build.query<
      {
        features: Array<{ id: string; featureKey: string; name?: string; module?: string }>;
        featureSets: Array<{
          id: string;
          name: string;
          orgId: string;
          isSystemDefault?: boolean;
          features?: FeatureSetFeatureRow[];
        }>;
      },
      void
    >({
      query: () => ({ document: FEATURES }),
      providesTags: ['Feature'],
    }),
    assignOrgFeatureSet: build.mutation<
      { assignOrgFeatureSet: boolean },
      { orgId: string; featureSetId: string }
    >({
      query: (variables) => ({ document: ASSIGN_ORG_FEATURE_SET, variables }),
      invalidatesTags: ['Feature', 'Org'],
    }),
    updateFeatureSetFeature: build.mutation<
      { updateFeatureSetFeature: FeatureSetFeatureRow },
      { id: string; displayName?: string | null; isEnabled?: boolean }
    >({
      query: (variables) => ({ document: UPDATE_FEATURE_SET_FEATURE, variables }),
      invalidatesTags: ['Feature', 'Org'],
    }),
    updateFeatureSetFeatures: build.mutation<
      { updateFeatureSetFeatures: FeatureSetFeatureRow[] },
      {
        featureSetId: string;
        items: Array<{ id: string; displayName?: string | null; isEnabled?: boolean }>;
      }
    >({
      query: (variables) => ({ document: UPDATE_FEATURE_SET_FEATURES, variables }),
      invalidatesTags: ['Feature', 'Org'],
    }),
    getTaskTemplates: build.query<
      { taskTemplates: TaskTemplate[] },
      { category?: string } | void
    >({
      query: (variables) => ({ document: TASK_TEMPLATES, variables: variables ?? {} }),
      providesTags: ['Task'],
    }),
    getTaskInstances: build.query<
      { taskInstances: TaskInstance[] },
      { orgId?: string; status?: string; category?: string } | void
    >({
      query: (variables) => ({ document: TASK_INSTANCES, variables: variables ?? {} }),
      providesTags: ['Task'],
    }),
    upsertTaskTemplate: build.mutation<
      { upsertTaskTemplate: TaskTemplate },
      Partial<TaskTemplate> & { code: string; title: string; category: string; kind: string; assigneeType: string; actionKey: string }
    >({
      query: (input) => ({ document: UPSERT_TASK_TEMPLATE, variables: { input } }),
      invalidatesTags: ['Task'],
    }),

    getBillingModules: build.query<{ billingModules: BillingModule[] }, void>({
      query: () => ({ document: BILLING_MODULES }),
      providesTags: ['Billing'],
    }),
    getBillingOverrides: build.query<
      { billingModulePriceOverrides: BillingModulePriceOverride[] },
      { tenantId: string }
    >({
      query: (variables) => ({ document: BILLING_OVERRIDES, variables }),
      providesTags: ['Billing'],
    }),
    getBillingBundles: build.query<{ billingPlanBundles: BillingPlanBundle[] }, void>({
      query: () => ({ document: BILLING_BUNDLES }),
      providesTags: ['Billing'],
    }),
    getBillingSubscriptions: build.query<
      { billingSubscriptions: BillingSubscription[] },
      { orgId?: string; status?: string } | void
    >({
      query: (variables) => ({
        document: BILLING_SUBSCRIPTIONS,
        variables: variables ?? {},
      }),
      providesTags: ['Billing'],
    }),
    getBillingInvoices: build.query<
      { billingInvoices: BillingInvoice[] },
      { orgId?: string; status?: string } | void
    >({
      query: (variables) => ({
        document: BILLING_INVOICES,
        variables: variables ?? {},
      }),
      providesTags: ['Billing'],
    }),
    upsertBillingModule: build.mutation<{ upsertBillingModule: BillingModule }, Record<string, unknown>>({
      query: (input) => ({ document: UPSERT_BILLING_MODULE, variables: { input } }),
      invalidatesTags: ['Billing'],
    }),
    upsertBillingOverride: build.mutation<
      { upsertBillingModulePriceOverride: BillingModulePriceOverride },
      Record<string, unknown>
    >({
      query: (input) => ({
        document: UPSERT_BILLING_OVERRIDE,
        variables: { input },
      }),
      invalidatesTags: ['Billing'],
    }),
    deleteBillingOverride: build.mutation<
      { deleteBillingModulePriceOverride: Record<string, unknown> },
      { id: string }
    >({
      query: (variables) => ({ document: DELETE_BILLING_OVERRIDE, variables }),
      invalidatesTags: ['Billing'],
    }),
    upsertBillingBundle: build.mutation<
      { upsertBillingPlanBundle: BillingPlanBundle },
      Record<string, unknown>
    >({
      query: (input) => ({ document: UPSERT_BILLING_BUNDLE, variables: { input } }),
      invalidatesTags: ['Billing'],
    }),
    createBillingSubscription: build.mutation<
      {
        createBillingSubscription: {
          subscription: BillingSubscription;
          invoice: BillingInvoice;
        };
      },
      Record<string, unknown>
    >({
      query: (input) => ({
        document: CREATE_BILLING_SUBSCRIPTION,
        variables: { input },
      }),
      invalidatesTags: ['Billing'],
    }),
    markBillingInvoicePaid: build.mutation<
      { markBillingInvoicePaid: BillingInvoice },
      { invoiceId: string }
    >({
      query: (variables) => ({ document: MARK_INVOICE_PAID, variables }),
      invalidatesTags: ['Billing'],
    }),
    voidBillingInvoice: build.mutation<
      { voidBillingInvoice: Record<string, unknown> },
      { invoiceId: string }
    >({
      query: (variables) => ({ document: VOID_INVOICE, variables }),
      invalidatesTags: ['Billing'],
    }),
    cancelBillingSubscription: build.mutation<
      { cancelBillingSubscription: BillingSubscription },
      { id: string }
    >({
      query: (variables) => ({ document: CANCEL_SUBSCRIPTION, variables }),
      invalidatesTags: ['Billing'],
    }),
    createCustomBillingInvoice: build.mutation<
      { createCustomBillingInvoice: BillingInvoice },
      {
        orgId: string;
        lines: Array<{ description: string; quantity: number; unitPrice: number }>;
        dueAt?: string | null;
        notes?: string | null;
      }
    >({
      query: (input) => ({ document: CREATE_CUSTOM_INVOICE, variables: { input } }),
      invalidatesTags: ['Billing', 'Org'],
    }),
    adminAddOrgLicense: build.mutation<
      { adminAddOrgLicense: BillingSubscription[] },
      {
        orgId: string;
        type: 'extra_seat' | 'entity.approvals';
        qty?: number;
        complimentary?: boolean;
        createInvoice?: boolean;
        extendPeriod?: boolean;
        billingPeriod?: 'monthly' | 'yearly';
      }
    >({
      query: (input) => ({ document: ADMIN_ADD_ORG_LICENSE, variables: { input } }),
      invalidatesTags: ['Billing', 'Org'],
    }),
    seedBillingDefaults: build.mutation<{ seedBillingDefaults: Record<string, unknown> }, void>({
      query: () => ({ document: SEED_BILLING_DEFAULTS }),
      invalidatesTags: ['Billing', 'Org'],
    }),
    upsertSystemNotification: build.mutation<
      { upsertSystemNotification: SystemNotification },
      Record<string, unknown>
    >({
      query: (input) => ({ document: UPSERT_SYSTEM_NOTIFICATION, variables: { input } }),
      invalidatesTags: ['Org'],
    }),
    deleteSystemNotification: build.mutation<
      { deleteSystemNotification: boolean },
      { id: string }
    >({
      query: (variables) => ({ document: DELETE_SYSTEM_NOTIFICATION, variables }),
      invalidatesTags: ['Org'],
    }),
    updateOrgBillingSettings: build.mutation<
      { updateOrgBillingSettings: Org },
      { orgId: string; warningDays?: number; graceDays?: number }
    >({
      query: (variables) => ({ document: UPDATE_ORG_BILLING_SETTINGS, variables }),
      invalidatesTags: ['Org'],
    }),
    unlockOrgOperations: build.mutation<{ unlockOrgOperations: Org }, { orgId: string }>({
      query: (variables) => ({ document: UNLOCK_ORG_OPERATIONS, variables }),
      invalidatesTags: ['Org'],
    }),
    adminUpdateOrgBranding: build.mutation<
      { adminUpdateOrgBranding: Org },
      { orgId: string; primaryColor?: string | null; logoFileId?: string | null }
    >({
      query: (variables) => ({ document: ADMIN_UPDATE_ORG_BRANDING, variables }),
      invalidatesTags: ['Org'],
    }),
    adminUpsertOrgSetting: build.mutation<
      { adminUpsertOrgSetting: OrgSettingRow },
      {
        orgId: string;
        code: string;
        valueType: string;
        booleanValue?: boolean | null;
        stringValue?: string | null;
        numberValue?: number | null;
        arrayValue?: unknown[] | null;
        enumOptions?: string[] | null;
      }
    >({
      query: (variables) => ({ document: ADMIN_UPSERT_ORG_SETTING, variables }),
      invalidatesTags: ['Org'],
    }),
  }),
});

export const {
  useSystemAdminLoginMutation,
  useSystemAdminInitMutation,
  useGetTenantsQuery,
  useGetOrgsQuery,
  useGetOrgDetailsQuery,
  useUpdateOrgStatusMutation,
  useCreateTenantMutation,
  useUpdateTenantConfigMutation,
  useAdminUpsertOrgConfigMutation,
  useCreateOrgMutation,
  useDeleteOrgMutation,
  useGetSystemAdminsQuery,
  useGetMasterRolesQuery,
  useAddUpdateMasterRoleMutation,
  useDeleteMasterRoleMutation,
  useCreateSystemAdminMutation,
  useUpdateSystemAdminMutation,
  useDeleteSystemAdminMutation,
  useUpdateSystemAdminProfileMutation,
  useChangeSystemAdminPasswordMutation,
  useGetAuditLogsQuery,
  useBroadcastOrgRefreshMutation,
  useGetFeaturesQuery,
  useAssignOrgFeatureSetMutation,
  useUpdateFeatureSetFeatureMutation,
  useUpdateFeatureSetFeaturesMutation,
  useGetTaskTemplatesQuery,
  useGetTaskInstancesQuery,
  useUpsertTaskTemplateMutation,
  useGetBillingModulesQuery,
  useGetBillingOverridesQuery,
  useGetBillingBundlesQuery,
  useGetBillingSubscriptionsQuery,
  useGetBillingInvoicesQuery,
  useUpsertBillingModuleMutation,
  useUpsertBillingOverrideMutation,
  useDeleteBillingOverrideMutation,
  useUpsertBillingBundleMutation,
  useCreateBillingSubscriptionMutation,
  useMarkBillingInvoicePaidMutation,
  useVoidBillingInvoiceMutation,
  useCancelBillingSubscriptionMutation,
  useCreateCustomBillingInvoiceMutation,
  useAdminAddOrgLicenseMutation,
  useSeedBillingDefaultsMutation,
  useUpsertSystemNotificationMutation,
  useDeleteSystemNotificationMutation,
  useUpdateOrgBillingSettingsMutation,
  useUnlockOrgOperationsMutation,
  useAdminUpdateOrgBrandingMutation,
  useAdminUpsertOrgSettingMutation,
} = adminApi;
